import React from "react";

import { useFormContext } from "react-hook-form";

import { PasswordInput } from "@components-shared/desktop/inputs";
import { PasswordSimilarityRequestModel } from "@data-types/membership/registration";
import { passwordValidationPattern } from "@gp/common";
import { useFieldValidation } from "@helpers/shared";
import { useValueAvailabilityDebounce } from "@state/hooks/shared";

export default function OnboardingRegistrationPassword(props: {
	checkPasswordSimilarity: (
		dataset: PasswordSimilarityRequestModel
	) => Promise<boolean>;
	labelClassName?: string;
}) {
	const { labelClassName } = props;
	const {
		formState: { errors },
		clearErrors,
		getValues,
	} = useFormContext();

	useFieldValidation("password", "confirmPassword");

	const debouncer = useValueAvailabilityDebounce();

	const passwordSimilarityCheck = async (passwordInput: string) => {
		const currentFormValues = getValues();
		const dataset: PasswordSimilarityRequestModel = {
			password: passwordInput,
			email: currentFormValues.email,
			dob: currentFormValues.dob,
			userName: currentFormValues.username,
			firstName: currentFormValues.firstName,
			lastName: currentFormValues.lastName,
		};

		const { password, ...valuesToCompareTo } = dataset;

		//if at least one field has values then check validity on BE
		const hasValidData = Object.values(valuesToCompareTo).some(
			(value) => value != null && value !== ""
		);

		if (!hasValidData) {
			//return no errors, form will be validated again on submit with new data
			return true;
		}

		return await props.checkPasswordSimilarity(dataset);
	};

	return (
		<>
			<PasswordInput
				id="enterPassword"
				className="form__field u-mar--bottom--reset"
				inputClassName="input input--sml"
				name="password"
				label="registration:PASSWORD_LABEL"
				labelClassName={labelClassName}
				placeholder="registration:PASSWORD_PLACEHOLDER"
				pattern={{
					value: passwordValidationPattern,
					message: "password-change:PASS_CHARACTERS_TW",
				}}
				validate={{
					isPasswordNotSimilar: async (value: string) => {
						if (errors["password"]) {
							clearErrors("password");
						}

						const isAvailable = await debouncer(
							value,
							passwordSimilarityCheck
						);

						if (isAvailable == null || isAvailable == undefined) {
							return true;
						}

						return (
							isAvailable ||
							"error-handling:ERROR_PWD_CHANGE_SIMILAR"
						);
					},
				}}
				required
			/>

			<PasswordInput
				id="repeatPassword"
				className="form__field u-mar--bottom--reset"
				inputClassName="input input--sml"
				name="confirmPassword"
				label="registration:CONFIRM_PASSWORD_LABEL"
				labelClassName={labelClassName}
				placeholder="registration:CONFIRM_PASSWORD_PLACEHOLDER"
				pattern={{
					value: passwordValidationPattern,
					message: "password-change:PASS_CHARACTERS_TW",
				}}
				validate={{
					isEqual: (value: string) => {
						const { password } = getValues();
						return (
							value === password ||
							"registration:DOES_NOT_MATCH_PWD"
						);
					},
				}}
				required
			/>
		</>
	);
}
